import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Send } from "lucide-react";
import Logo from "./Logo";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "cw_cora_session_v1";
const WELCOME = {
  role: "assistant",
  text:
    "Hi, I'm Cora — Research Assistant at Certified Wellness. I can help with product questions, purity/COA, shipping, or point you to the right compound for your research. How can I help today?",
};

const QUICK = [
  "What's best for metabolic research?",
  "How fast do you ship?",
  "Do you have a COA on every batch?",
  "Can I get a discount code?",
];

const CoraChat = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || !sessionId) return;
    axios
      .get(`${BASE}/chat/${sessionId}`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          setMessages([WELCOME, ...r.data]);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", text: trimmed }]);
    setInput("");
    try {
      const { data } = await axios.post(
        `${BASE}/chat`,
        { message: trimmed, sessionId: sessionId || undefined },
        { timeout: 45000 }
      );
      if (data.sessionId && data.sessionId !== sessionId) {
        setSessionId(data.sessionId);
        localStorage.setItem(SESSION_KEY, data.sessionId);
      }
      setMessages((m) => [...m, { role: "assistant", text: data.message }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            "I'm having a bit of trouble right now. Please try again, or email support@certifiedwellness.net.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Chat with Cora"
          data-testid="cora-open-btn"
          className="cora-fab fixed bottom-6 left-6 z-40 group flex items-center gap-3 h-[72px] pl-[6px] pr-6 bg-[#0a0a0a] text-white shadow-2xl hover:shadow-[0_20px_60px_rgba(185,28,28,0.45)] transition-shadow"
        >
          <span className="relative flex items-center justify-center h-[60px] w-[60px] rounded-full bg-white shrink-0 ring-2 ring-[#b91c1c] ring-offset-2 ring-offset-[#0a0a0a] group-hover:ring-[#fafbfc] transition-all">
            <Logo size={56} />
            <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-green-400 border-[3px] border-[#0a0a0a] animate-pulse" />
          </span>
          <span className="flex flex-col items-start leading-none text-left">
            <span className="text-[9px] font-semibold uppercase tracking-[0.32em] text-red-400">
              Online · Now
            </span>
            <span className="mt-1.5 text-[15px] font-semibold tracking-[0.06em]">
              Ask <span className="font-serif italic font-normal">Cora</span>
            </span>
          </span>
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 md:inset-auto md:bottom-6 md:left-6 z-50 md:w-[400px] md:h-[620px] bg-white shadow-2xl flex flex-col border border-gray-200"
          data-testid="cora-panel"
        >
          {/* Header */}
          <div className="relative bg-[#0a0a0a] text-white px-5 py-4 flex items-center gap-3">
            <div className="relative flex items-center justify-center h-14 w-14 rounded-full bg-white ring-2 ring-[#b91c1c] ring-offset-2 ring-offset-[#0a0a0a] shrink-0">
              <Logo size={50} />
              <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-green-400 border-[3px] border-[#0a0a0a]" />
            </div>
            <div className="flex-1">
              <div className="font-display text-base font-semibold leading-tight">Cora</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-red-400">
                Research Assistant · Online
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="cora-close-btn"
              className="text-gray-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-5 space-y-4 bg-[#fafbfc]"
            data-testid="cora-messages"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-line ${
                    m.role === "user"
                      ? "bg-[#0a0a0a] text-white"
                      : "bg-white border border-gray-200 text-[#0a0a0a]"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 px-4 py-3 flex gap-1" data-testid="cora-typing">
                  <span className="h-2 w-2 rounded-full bg-[#b91c1c] animate-bounce" />
                  <span className="h-2 w-2 rounded-full bg-[#b91c1c] animate-bounce [animation-delay:120ms]" />
                  <span className="h-2 w-2 rounded-full bg-[#b91c1c] animate-bounce [animation-delay:240ms]" />
                </div>
              </div>
            )}
          </div>

          {messages.length <= 1 && !sending && (
            <div className="px-4 pb-3 pt-1 flex flex-wrap gap-2 bg-[#fafbfc]">
              {QUICK.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="text-[11px] font-medium text-[#0a0a0a] bg-white border border-gray-200 hover:border-[#0a0a0a] px-3 py-1.5 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="flex items-end gap-2 px-4 py-3 border-t border-gray-200 bg-white"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              rows={1}
              placeholder="Ask Cora a question…"
              className="flex-1 resize-none bg-[#fafbfc] border border-gray-200 focus:border-[#0a0a0a] focus:outline-none text-sm px-3 py-2 max-h-28"
              data-testid="cora-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              aria-label="Send"
              data-testid="cora-send-btn"
              className="h-10 w-10 bg-[#b91c1c] text-white hover:bg-[#a01818] disabled:bg-gray-300 transition-colors inline-flex items-center justify-center"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
          <div className="px-4 pb-3 text-[10px] uppercase tracking-[0.14em] text-gray-400 bg-white">
            Cora provides research info only · not medical advice
          </div>
        </div>
      )}
    </>
  );
};

export default CoraChat;
